// Рубильник «Запуска» (задача D15) — первая строка экрана, одно место для остановки и включения.
//
// - `StopBlock` (только admin): «Остановить Анну на всех каналах — чаты сразу уйдут людям.» и «Остановить». Наверху, а не
//   в конце экрана: остановка — безопасное направление (сразу и с отменой), прятать её незачем, а внизу на телефоне она
//   была бы в двух-трёх экранах прокрутки. Тихий вид: строка без фона и иконки, кнопка — обводка `--destructive`, а не
//   красная заливка (ux-engine «Опасные действия»). Нажатие — сразу, без окна; тост «Анна остановлена · Отменить» (10 с),
//   остановку объявляет чтению с экрана пилюля статуса (`assertive`). Кнопка не выключается и не пропадает, даже пока
//   экран грузится или не загрузился: остановку сервер принимает без сверки версии (Ruling 130), ждать экран незачем.
// - `StoppedBanner` — на том же месте, когда Анна стоит: «○ Анна остановлена 05.10 14:20 МСК (Влад Т.). Чаты идут людям.»
//   (остановили не из пульта, имени нет — без скобок) и что ступень, доля и каналы ниже вступят в силу после включения. У
//   админа — «Включить снова» в обводке: это повышение, через окно «Включить Анну снова?». Залитой кнопки на экране нет:
//   каждый постоянный контрол здесь меняет, сколько клиентов видят Анну.
import type { LaunchState } from '@contract/index.js';
import type { Ref, RefObject } from 'react';
import { PendingButton } from '@/components/pending-button';
import { StatusMark } from '@/components/status-dot';
import { Button } from '@/components/ui/button';
import { formatMoment } from '@/lib/format';

export const STOP_TEXT_RU = 'Остановить Анну на всех каналах — чаты сразу уйдут людям.';
export const STOPPED_NOTE_RU = 'Ступень, доля и каналы ниже вступят в силу после «Включить снова».';

/** Кнопка остановки: тихая, обводка `--destructive` (та же в поповере пилюли). */
export const STOP_BUTTON_CLASS = 'border-destructive text-st-error hover:bg-st-error-soft';

export function StopBlock({ pending, onStop }: { readonly pending: boolean; readonly onStop: () => void }) {
  return (
    <div data-slot="stop-block" className="flex items-center justify-between gap-4 py-1">
      <p className="min-w-0 text-pretty text-muted-foreground">{STOP_TEXT_RU}</p>
      <PendingButton pending={pending} variant="outline" onClick={onStop} className={STOP_BUTTON_CLASS}>
        Остановить
      </PendingButton>
    </div>
  );
}

/** Текст полосы: когда и кем остановлена; имени нет — без скобок. */
export function stoppedLineRu(stopped: NonNullable<LaunchState['stopped']>): string {
  const by = stopped.byName.trim() === '' ? '' : ` (${stopped.byName})`;
  return `Анна остановлена ${formatMoment(stopped.at)}${by}. Чаты идут людям.`;
}

export interface StoppedBannerProps {
  readonly stopped: NonNullable<LaunchState['stopped']>;
  readonly admin: boolean;
  readonly onResume: () => void;
  readonly resumeRef?: RefObject<HTMLButtonElement | null>;
  /** Сама полоса: на неё встаёт фокус после остановки (кнопки «Остановить» уже нет). */
  readonly ref?: Ref<HTMLDivElement>;
}

/** Полоса «Анна остановлена». */
export function StoppedBanner({ stopped, admin, onResume, resumeRef, ref }: StoppedBannerProps) {
  return (
    <div
      ref={ref}
      tabIndex={-1}
      data-slot="stopped-banner"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg bg-muted px-4 py-3 outline-none max-sm:px-3"
    >
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="flex items-baseline gap-2 text-pretty">
          <span className="relative top-px">
            <StatusMark tone="reject" hollow />
          </span>
          <span>{stoppedLineRu(stopped)}</span>
        </p>
        <p className="pl-4 text-sm text-pretty text-muted-foreground">{STOPPED_NOTE_RU}</p>
      </div>
      {admin && (
        <Button ref={resumeRef} variant="outline" onClick={onResume} className="max-sm:h-10 max-sm:w-full">
          Включить снова
        </Button>
      )}
    </div>
  );
}
